import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Mail, Send, Inbox, MailOpen, Paperclip, FileText, X, Reply, Pencil, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagesPage() {
  const { token, user, isClient, isAnalyst, isAdmin } = useAuth();
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyMode, setReplyMode] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [filterBureau, setFilterBureau] = useState('');
  const [filterAnalyst, setFilterAnalyst] = useState('');
  const [offices, setOffices] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [newMessage, setNewMessage] = useState({
    recipient_id: '',
    subject: '',
    content: ''
  });
  const [attachment, setAttachment] = useState(null);
  const isSupervisor = user?.role === 'super_admin' || user?.role === 'admin_ops' || user?.role === 'bureau';

  useEffect(() => {
    fetchData();
    if (isSupervisor) {
      axios.get(`${API}/offices`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setOffices(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      axios.get(`${API}/analysts`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setAnalysts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }
  }, [token]);

  useEffect(() => {
    if (isSupervisor) fetchData();
  }, [filterBureau, filterAnalyst]);

  const fetchData = async () => {
    try {
      let msgUrl = `${API}/messages`;
      const params = [];
      if (filterBureau) params.push(`bureau_id=${filterBureau}`);
      if (filterAnalyst) params.push(`analyst_id=${filterAnalyst}`);
      if (params.length) msgUrl += '?' + params.join('&');

      const [messagesRes, usersRes] = await Promise.all([
        axios.get(msgUrl, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        // Get potential recipients based on role
        isClient ? 
          axios.get(`${API}/clients/${user.id}`, { headers: { Authorization: `Bearer ${token}` } }) :
          axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setMessages(messagesRes.data);
      
      if (isClient) {
        // Client can only message their analyst
        if (usersRes.data.analyst) {
          setUsers([usersRes.data.analyst]);
        }
      } else {
        setUsers(usersRes.data.filter(u => u.id !== user.id));
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.recipient_id || !newMessage.subject || !newMessage.content) {
      toast.error(t('error'), { description: 'Veuillez remplir tous les champs' });
      return;
    }

    try {
      const payload = { ...newMessage };
      if (attachment) {
        payload.attachment = attachment.data;
        payload.attachment_name = attachment.name;
      }
      await axios.post(`${API}/messages`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('success'), { description: 'Message envoyé' });
      setComposeOpen(false);
      setNewMessage({ recipient_id: '', subject: '', content: '' });
      setAttachment(null);
      fetchData();
    } catch (error) {
      toast.error(t('error'), { description: 'Erreur lors de l\'envoi' });
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAttachment({ name: file.name, size: file.size, data: ev.target.result });
    };
    reader.readAsDataURL(file);
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, read: true } : m));
      setSelectedMessage(prev => prev && prev.id === messageId ? { ...prev, read: true } : prev);
      window.dispatchEvent(new Event('notifications-updated'));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleEditMessage = async () => {
    if (!selectedMessage) return;
    try {
      await axios.put(`${API}/messages/${selectedMessage.id}`, {
        subject: editSubject,
        content: editContent
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Message modifié');
      setEditMode(false);
      setSelectedMessage(prev => ({ ...prev, subject: editSubject, content: editContent, edited_at: new Date().toISOString() }));
      setMessages(prev => prev.map(m => m.id === selectedMessage.id ? { ...m, subject: editSubject, content: editContent, edited_at: new Date().toISOString() } : m));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const startEdit = () => {
    setEditSubject(selectedMessage.subject);
    setEditContent(selectedMessage.content);
    setEditMode(true);
    setReplyMode(false);
  };

  // For supervisors viewing all messages, show everything in one list
  const inbox = isSupervisor 
    ? messages 
    : messages.filter(m => m.recipient_id === user.id);
  const sent = isSupervisor 
    ? [] 
    : messages.filter(m => m.sender_id === user.id);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('messages')}
          </h2>
          <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
            <DialogTrigger asChild>
              <Button className="btn-gold" data-testid="compose-btn">
                <Send className="w-4 h-4 mr-2" />
                {t('compose')}
              </Button>
            </DialogTrigger>
            <DialogContent className="glass">
              <DialogHeader>
                <DialogTitle>Nouveau message</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Destinataire</Label>
                  <Select
                    value={newMessage.recipient_id}
                    onValueChange={(value) => setNewMessage(prev => ({ ...prev, recipient_id: value }))}
                  >
                    <SelectTrigger className="bg-card/50">
                      <SelectValue placeholder="Sélectionner un destinataire" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('subject')}</Label>
                  <Input
                    value={newMessage.subject}
                    onChange={(e) => setNewMessage(prev => ({ ...prev, subject: e.target.value }))}
                    className="bg-card/50"
                    data-testid="message-subject"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('message')}</Label>
                  <Textarea
                    value={newMessage.content}
                    onChange={(e) => setNewMessage(prev => ({ ...prev, content: e.target.value }))}
                    rows={8}
                    className="bg-card/50 resize-y"
                    data-testid="message-content"
                  />
                </div>
                {/* Attachment */}
                <div>
                  <input type="file" id="msg-file-input" className="hidden" onChange={handleFileSelect} />
                  {attachment ? (
                    <div className="flex items-center gap-2 p-2 rounded border bg-secondary/30">
                      <FileText className="w-4 h-4 text-primary shrink-0" />
                      <span className="text-sm truncate flex-1">{attachment.name}</span>
                      <span className="text-xs text-muted-foreground">{(attachment.size / 1024).toFixed(0)} KB</span>
                      <button onClick={() => setAttachment(null)} className="text-muted-foreground hover:text-foreground">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => document.getElementById('msg-file-input').click()} className="w-full" data-testid="attach-file-btn">
                      <Paperclip className="w-4 h-4 mr-2" />
                      Joindre un fichier (max 10 MB)
                    </Button>
                  )}
                </div>
                <Button onClick={handleSendMessage} className="w-full btn-gold" data-testid="send-message-btn">
                  {t('send')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters for supervisors */}
        {isSupervisor && (
          <div className="flex gap-3 items-center flex-wrap">
            {(user?.role === 'super_admin' || user?.role === 'admin_ops') && (
              <select
                value={filterBureau}
                onChange={(e) => { setFilterBureau(e.target.value); setFilterAnalyst(''); }}
                className="h-8 rounded-md border bg-card text-sm px-2"
                data-testid="filter-bureau"
              >
                <option value="">Tous les bureaux</option>
                {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}
            <select
              value={filterAnalyst}
              onChange={(e) => setFilterAnalyst(e.target.value)}
              className="h-8 rounded-md border bg-card text-sm px-2"
              data-testid="filter-analyst"
            >
              <option value="">Tous les analystes</option>
              {analysts
                .filter(a => !filterBureau || a.office_id === filterBureau)
                .map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
            </select>
            <span className="text-xs text-muted-foreground">{messages.length} message{messages.length > 1 ? 's' : ''}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Message List */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="card-premium">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Inbox className="w-4 h-4" />
                  {isSupervisor ? `Tous les messages (${inbox.length})` : `${t('inbox')} (${inbox.length})`}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {inbox.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">{t('no_data')}</p>
                ) : (
                  inbox.map(msg => (
                    <button
                      key={msg.id}
                      onClick={() => {
                        setSelectedMessage(msg); setReplyMode(false); setReplyContent('');
                        if (!msg.read) handleMarkAsRead(msg.id);
                      }}
                      className={`w-full text-left p-3 rounded-sm transition-colors ${
                        selectedMessage?.id === msg.id ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium truncate">
                          {isSupervisor 
                            ? `${msg.sender_name || 'Inconnu'} → ${msg.recipient_name || 'Inconnu'}`
                            : (msg.sender_name || 'Inconnu')
                          }
                        </span>
                        {!msg.read && (
                          <Badge className="status-active text-xs">Nouveau</Badge>
                        )}
                      </div>
                      <p className="text-sm truncate flex items-center gap-1">{msg.attachment_name && <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />}{msg.subject}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </p>
                    </button>
                  ))
                )}
              </CardContent>
            </Card>

            {!isSupervisor && (
            <Card className="card-premium">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  {t('sent')} ({sent.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {sent.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">{t('no_data')}</p>
                ) : (
                  sent.map(msg => (
                    <button
                      key={msg.id}
                      onClick={() => { setSelectedMessage(msg); setReplyMode(false); setReplyContent(''); if (!msg.read && msg.recipient_id === user.id) handleMarkAsRead(msg.id); }}
                      className={`w-full text-left p-3 rounded-sm transition-colors ${
                        selectedMessage?.id === msg.id ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium truncate">
                          À: {msg.recipient_name || 'Inconnu'}
                        </span>
                      </div>
                      <p className="text-sm truncate flex items-center gap-1">{msg.attachment_name && <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />}{msg.subject}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </p>
                    </button>
                  ))
                )}
              </CardContent>
            </Card>
            )}
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-3">
            <Card className="card-premium h-full">
              <CardContent className="p-6">
                {selectedMessage ? (
                  <div className="space-y-4">
                    <div className="border-b border-border pb-4">
                      {editMode ? (
                        <Input
                          value={editSubject}
                          onChange={(e) => setEditSubject(e.target.value)}
                          className="text-xl font-semibold mb-2"
                          data-testid="edit-message-subject"
                        />
                      ) : (
                        <h3 className="text-xl font-semibold mb-2">{selectedMessage.subject}</h3>
                      )}
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>
                          De: {selectedMessage.sender_name || 'Inconnu'}
                        </span>
                        <div className="flex items-center gap-2">
                          <span>
                            {new Date(selectedMessage.created_at).toLocaleDateString()} à{' '}
                            {new Date(selectedMessage.created_at).toLocaleTimeString()}
                          </span>
                          {selectedMessage.edited_at && !isClient && (
                            <span className="text-xs italic">(modifié)</span>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        À: {selectedMessage.recipient_name || 'Inconnu'}
                      </div>
                    </div>
                    {editMode ? (
                      <div className="space-y-3">
                        <Textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          rows={8}
                          data-testid="edit-message-content"
                        />
                        <div className="flex gap-2">
                          <Button onClick={handleEditMessage} size="sm" className="bg-green-600 hover:bg-green-700" data-testid="edit-message-save">
                            <Check className="w-4 h-4 mr-1" /> Enregistrer
                          </Button>
                          <Button onClick={() => setEditMode(false)} size="sm" variant="outline">
                            Annuler
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="prose prose-invert max-w-none">
                        <p className="whitespace-pre-wrap">{selectedMessage.content}</p>
                      </div>
                    )}
                    {selectedMessage.attachment && selectedMessage.attachment_name && (
                      <div className="pt-3 border-t border-border">
                        <p className="text-xs text-muted-foreground mb-2">Pièce jointe :</p>
                        <a 
                          href={selectedMessage.attachment} 
                          download={selectedMessage.attachment_name}
                          className="flex items-center gap-2 p-3 rounded border bg-secondary/30 hover:bg-secondary/50 transition-colors"
                          data-testid="attachment-download"
                        >
                          <FileText className="w-5 h-5 text-primary shrink-0" />
                          <span className="text-sm font-medium truncate">{selectedMessage.attachment_name}</span>
                          <span className="text-xs text-muted-foreground ml-auto">Télécharger</span>
                        </a>
                      </div>
                    )}
                    {/* Reply & Edit buttons */}
                    {!editMode && (
                    <div className="pt-3 border-t border-border mt-3 flex gap-2">
                      {!replyMode ? (
                        <>
                          <Button variant="outline" size="sm" onClick={() => { setReplyMode(true); setEditMode(false); }} data-testid="reply-btn">
                            <Reply className="w-4 h-4 mr-2" />
                            Répondre
                          </Button>
                          {selectedMessage.sender_id === user.id && !isClient && (
                            <Button variant="outline" size="sm" onClick={startEdit} data-testid="edit-message-btn">
                              <Pencil className="w-4 h-4 mr-2" />
                              Modifier
                            </Button>
                          )}
                        </>
                      ) : (
                        <div className="space-y-2 w-full">
                          <textarea
                            className="w-full p-3 rounded-lg border border-input bg-background text-sm min-h-[200px] outline-none focus:ring-1 focus:ring-ring resize-y"
                            placeholder="Votre réponse..."
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            data-testid="reply-textarea"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={async () => {
                              if (!replyContent.trim()) return;
                              try {
                                await axios.post(`${API}/messages`, {
                                  recipient_id: selectedMessage.sender_id === user.id ? selectedMessage.recipient_id : selectedMessage.sender_id,
                                  subject: `Re: ${selectedMessage.subject}`,
                                  content: replyContent,
                                }, { headers: { Authorization: `Bearer ${token}` } });
                                toast.success('Réponse envoyée');
                                setReplyMode(false);
                                setReplyContent('');
                                fetchData();
                              } catch { toast.error('Erreur'); }
                            }} data-testid="send-reply-btn">Envoyer</Button>
                            <Button variant="outline" size="sm" onClick={() => { setReplyMode(false); setReplyContent(''); }}>Annuler</Button>
                          </div>
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                    <MailOpen className="w-12 h-12 mb-4" />
                    <p>Sélectionnez un message pour le lire</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
